import React, { useState, useEffect } from 'react';
import { Loading, Box, Feedback } from '@cubeyou-team/hx-react-components';
import styled from 'styled-components';

const StyledMicroFrontendContainer = styled.main`
  height: 100%;
  overflow: hidden;
`;

const toContainerId = (name, instanceId) => `${name}-container-${instanceId}`;

const getScriptKey = name => `mfe-script-${name}`;
const scriptIsLoaded = name => window[getScriptKey(name)] === true;
const setScriptIsLoaded = name => (window[getScriptKey(name)] = true);

const renderMicroFrontend = (
  name,
  window,
  history,
  instanceId,
  query,
  repositoryCollapsed,
  state,
  external
) => {
  const renderAppFunction = window[`render${name}`];
  if (renderAppFunction) {
    renderAppFunction(
      toContainerId(name, instanceId),
      history,
      query,
      instanceId,
      repositoryCollapsed,
      state,
      external
    );
  }
};

const MicroFrontend = ({
  document,
  window,
  name,
  history,
  host,
  instanceId,
  query,
  state,
  onError,
  repositoryCollapsed,
  external,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [errorLoadingMFE, SetErrorLoadingMFE] = useState(false);

  const effectCleanup = () => {
    setIsLoaded(false);
    const unmountFunction = window[`unmount${name}`];
    if (unmountFunction) {
      unmountFunction(toContainerId(name, instanceId));
    }
  };

  useEffect(() => {
    const scriptId = `micro-frontend-script-${name}`;
    if (document.getElementById(scriptId)) {
      let c = 1;
      const max = 5;
      const loadingInterval = setInterval(() => {
        if (scriptIsLoaded(name)) {
          renderMicroFrontend(
            name,
            window,
            history,
            instanceId,
            query,
            repositoryCollapsed,
            state,
            external
          );
          setIsLoaded(true);
          clearInterval(loadingInterval);
        }
        c++;
        if (c >= max) {
          clearInterval(loadingInterval);
        }
      }, c * 500);
      return effectCleanup;
    }

    const timestamp = new Date().getTime();

    if (!window.Cypress) {
      fetch(`${host}/asset-manifest.json?cache=${timestamp}`)
        .then(res => res.json())
        .then(manifest => {
          let assetsCount = 0;
          let totalAssets = 0;

          // Count total assets to load (both JS and CSS)
          manifest.entrypoints.forEach(chunk => {
            if (chunk.endsWith('.js') || chunk.endsWith('.css')) {
              totalAssets++;
            }
          });

          // If no assets to load, set loaded immediately
          if (totalAssets === 0) {
            setScriptIsLoaded(name);
            renderMicroFrontend(
              name,
              window,
              history,
              instanceId,
              query,
              repositoryCollapsed,
              state,
              external
            );
            setIsLoaded(true);
            return;
          }

          manifest.entrypoints.forEach(chunk => {
            if (chunk.endsWith('.js')) {
              assetsCount++;
              const script = document.createElement('script');
              script.id = scriptId;
              // Only set crossOrigin if host is different origin to avoid CORS issues
              // Empty string crossOrigin can cause false positive cross-origin errors
              try {
                if (
                  host &&
                  new URL(host, window.location.href).origin !==
                    window.location.origin
                ) {
                  script.crossOrigin = 'anonymous';
                }
              } catch (e) {
                // If URL parsing fails, don't set crossOrigin (default behavior)
              }
              script.src = `${host}/${chunk}`;
              script.onload = () => {
                if (--assetsCount === 0) {
                  setScriptIsLoaded(name);
                  renderMicroFrontend(
                    name,
                    window,
                    history,
                    instanceId,
                    query,
                    repositoryCollapsed,
                    state,
                    external
                  );
                  setIsLoaded(true);
                }
              };
              document.head.appendChild(script);
            } else if (chunk.endsWith('.css')) {
              assetsCount++;
              const link = document.createElement('link');
              link.rel = 'stylesheet';
              link.href = `${host}/${chunk}`;
              link.onload = () => {
                if (--assetsCount === 0) {
                  setScriptIsLoaded(name);
                  renderMicroFrontend(
                    name,
                    window,
                    history,
                    instanceId,
                    query,
                    repositoryCollapsed,
                    state,
                    external
                  );
                  setIsLoaded(true);
                }
              };
              link.onerror = () => {
                // If CSS fails to load, still count it as loaded to prevent blocking
                if (--assetsCount === 0) {
                  setScriptIsLoaded(name);
                  renderMicroFrontend(
                    name,
                    window,
                    history,
                    instanceId,
                    query,
                    repositoryCollapsed,
                    state,
                    external
                  );
                  setIsLoaded(true);
                }
              };
              document.head.appendChild(link);
            }
          });
        })
        .catch(() => {
          SetErrorLoadingMFE(true);
          setIsLoaded(true);
          onError && onError(instanceId, query);
        });
    }

    return effectCleanup;
  }, []);

  return (
    <Box style={{ height: '100%' }}>
      <Loading isLoading={!isLoaded} hideMode>
        {errorLoadingMFE && (
          <StyledMicroFrontendContainer name={name}>
            <Box pX="medium" pY="medium">
              <Feedback
                status="error"
                message="An error accoured, close this tab and try again."
              />
            </Box>
          </StyledMicroFrontendContainer>
        )}
        <StyledMicroFrontendContainer
          id={toContainerId(name, instanceId)}
          name={name}
        />
      </Loading>
    </Box>
  );
};

MicroFrontend.defaultProps = {
  document,
  window,
};

export default MicroFrontend;
